import React, { useState } from "react";
import { BRAND_LOGO, BRAND_LOGO_INVERT, BRAND_NAME, BRAND_TAGLINE } from "../brand.js";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import { SSO_PROVIDERS, startSso } from "../auth/Sso.js";

const PASSWORD_HINT = "At least 12 characters, with uppercase, lowercase, a number, and a symbol.";

/** The brand half of the sign-in screen: whose dashboard this is, and what it is for. */
function BrandPanel() {
  return (
    <aside className="login-brand">
      {BRAND_LOGO ? (
        <img
          src={BRAND_LOGO}
          alt={BRAND_NAME}
          className={`login-brand-logo${BRAND_LOGO_INVERT ? " logo-invert" : ""}`}
        />
      ) : (
        <span className="login-brand-wordmark">{BRAND_NAME}</span>
      )}
      <div>
        <h1 className="login-brand-title">AWS cost and usage</h1>
        <p className="login-brand-tagline">{BRAND_TAGLINE}</p>
      </div>
      <div className="login-brand-foot">
        © {new Date().getFullYear()} {BRAND_NAME}
      </div>
    </aside>
  );
}

export default function Login() {
  const { session, login, error, newPasswordChallenge, completeNewPassword, cancelNewPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [ssoStarting, setSsoStarting] = useState(null);
  // A refused Google/Microsoft sign-in comes back here with its reason.
  const ssoError = useLocation().state?.ssoError;

  if (session) return <Navigate to="/" replace />;

  const onSso = (provider) => {
    setSsoStarting(provider);
    startSso(provider).catch(() => setSsoStarting(null));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await login(email, password);
    setSubmitting(false);
  };

  const onSetNewPassword = async (e) => {
    e.preventDefault();
    setLocalError(null);
    if (newPassword !== confirmPassword) {
      setLocalError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 12) {
      setLocalError("Password must be at least 12 characters.");
      return;
    }
    setSubmitting(true);
    const ok = await completeNewPassword(newPassword);
    setSubmitting(false);
    if (ok) {
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  const onBackToSignIn = () => {
    cancelNewPassword();
    setPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setLocalError(null);
  };

  if (newPasswordChallenge) {
    return (
      <div className="login-shell">
        <BrandPanel />
        <main className="login-main">
          <form className="login-card" onSubmit={onSetNewPassword}>
            <h2>Set a new password</h2>
            <p className="login-subtitle">
              This is your first sign-in — choose a permanent password to replace the temporary one Cognito emailed you.
            </p>
            <div className="field">
              <label htmlFor="new-password">New password</label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={12}
                autoFocus
                autoComplete="new-password"
                required
              />
            </div>
            <div className="field">
              <label htmlFor="confirm-password">Confirm new password</label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={12}
                autoComplete="new-password"
                required
              />
            </div>
            <p className="field-hint">{PASSWORD_HINT}</p>
            {(localError || error) && <div className="error-banner">{localError || error}</div>}
            <button className="primary" type="submit" disabled={submitting} style={{ width: "100%" }}>
              {submitting ? "Setting password…" : "Set password & sign in"}
            </button>
            <button
              type="button"
              className="link"
              onClick={onBackToSignIn}
              style={{ width: "100%", marginTop: 14, textAlign: "center" }}
            >
              Back to sign in
            </button>
          </form>
        </main>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <BrandPanel />
      <main className="login-main">
        <form className="login-card" onSubmit={onSubmit}>
          <h2>Sign in</h2>
          <p className="login-subtitle">
            {SSO_PROVIDERS.length > 0
              ? "Use the work account your administrator registered for you."
              : "Use the email address your administrator registered for you."}
          </p>
          {ssoError && <div className="error-banner">{ssoError}</div>}
          {SSO_PROVIDERS.length > 0 && (
            <>
              <div className="sso-buttons">
                {SSO_PROVIDERS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="sso-button"
                    onClick={() => onSso(p.id)}
                    disabled={!!ssoStarting}
                  >
                    {ssoStarting === p.id ? `Opening ${p.label}…` : `Continue with ${p.label}`}
                  </button>
                ))}
              </div>
              <div className="login-divider">
                <span>or sign in with a password</span>
              </div>
            </>
          )}
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus={SSO_PROVIDERS.length === 0}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          {error && <div className="error-banner">{error}</div>}
          <button className="primary" type="submit" disabled={submitting} style={{ width: "100%" }}>
            {submitting ? "Signing in…" : "Sign in"}
          </button>
          <p className="field-hint" style={{ margin: "18px 0 0" }}>
            No account? Ask an administrator to add you. There is no self sign-up.
          </p>
        </form>
      </main>
    </div>
  );
}
